import { useCallback, useEffect, useState } from 'react';
import axiosInstance from '../variables/api/axiosInstance';

/**
 * Taux de retenue à la source — référentiel paramétrable (Comptabilité → Fiscalité).
 *
 * Remplace les listes « 3 % / 5 % / 20 % » recopiées en dur dans cinq écrans : un
 * taux ajouté ou modifié en paramétrage apparaît partout, sans redéploiement.
 *
 * Les experts et les lots portent un CODE (« CINQ_POURCENT », « TAUX_1A2B3C4D »)
 * qui n'est jamais montré ; on affiche `affichage`, reformaté par `formaterTaux`
 * en « 5 % » ou « Sans NIF (5 %) ».
 */

const URL = '/v1/comptabilite/parametrage/taux-retenue';

// Un seul appel par chargement de page, partagé par tous les écrans ouverts.
let cache = null;

export const chargerTauxRetenue = (forcer = false) => {
  if (!cache || forcer) {
    cache = axiosInstance.get(URL)
      .then((r) => r.data?.data || [])
      .catch((e) => {
        cache = null; // ne pas garder un échec en mémoire
        throw e;
      });
  }
  return cache;
};

/** « 5 % » pour une fraction 0.05 — même règle que le serveur (virgule, sans zéros inutiles). */
export const pourcent = (fraction) => {
  if (fraction == null || fraction === '') return null;
  const p = Math.round(Number(fraction) * 100 * 100) / 100;
  return `${String(p).replace('.', ',')} %`;
};

/**
 * « Sans NIF (5 %) » la ou le serveur dit « 5 % — Sans NIF ».
 *
 * <p>LE POURCENTAGE PASSE ENTRE PARENTHESES, DERRIERE LA DESIGNATION. Le serveur
 * assemble « pourcentage — designation » : c'est le chiffre qui se lisait d'abord, et la
 * designation - « Sans NIF », « Prestataires etrangers » - venait apres un tiret, comme
 * une precision secondaire. C'est pourtant elle qui nomme le taux ; le pourcentage en
 * est la valeur, et se lit entre parentheses.
 *
 * <p>Sans designation, la chaine est deja le pourcentage seul : rien a reformater. Un
 * taux absent du referentiel arrive sous la forme de son code (« CINQ_POURCENT ») et
 * traverse tel quel, faute de mieux.
 */
export const formaterTaux = (affichage) => {
  if (!affichage) return affichage;
  const separateur = affichage.indexOf(' — ');
  if (separateur === -1) return affichage;
  const pourcentage = affichage.slice(0, separateur).trim();
  const designation = affichage.slice(separateur + 3).trim();
  // Une designation vide laisse le pourcentage seul, plutot qu'un « 5 % () » absurde.
  return designation ? `${designation} (${pourcentage})` : pourcentage;
};

export const tauxRetenueApi = {
  lister: () => axiosInstance.get(URL).then((r) => r.data?.data || []),
  creer: (corps) => axiosInstance.post(URL, corps),
  modifier: (id, corps) => axiosInstance.put(`${URL}/${id}`, corps),
  supprimer: (id) => axiosInstance.delete(`${URL}/${id}`),
};

/**
 * Accès aux taux depuis un composant.
 *   - `options`   : taux EN VIGUEUR, pour les listes de choix ;
 *   - `affichage` : code → « 5 % » (taux désactivés compris : un vieux lot doit rester lisible) ;
 *   - `valeur`    : code → fraction, pour les calculs d'aperçu.
 */
export function useTauxRetenue() {
  const [taux, setTaux] = useState([]);
  const [charge, setCharge] = useState(false);

  const recharger = useCallback((forcer = true) => chargerTauxRetenue(forcer)
    .then((liste) => { setTaux(liste); setCharge(true); return liste; })
    .catch(() => { setCharge(true); return []; }), []);

  useEffect(() => { recharger(false); }, [recharger]);

  const parCode = Object.fromEntries(taux.map((t) => [t.code, t]));
  return {
    taux,
    charge,
    recharger,
    options: taux.filter((t) => t.actif)
      .map((t) => ({ value: t.code, label: formaterTaux(t.affichage), valeur: Number(t.taux) })),
    affichage: (code) => (code ? formaterTaux(parCode[code]?.affichage || code) : '—'),
    valeur: (code) => (parCode[code] ? Number(parCode[code].taux) : null),
  };
}
